/**
 * Modus-Registry (datengetrieben).
 *
 * Ein Kurs DEKLARIERT, welche Modi er unterstützt (Course.modes). Die
 * Umschalter im Kurs-Screen erscheinen dynamisch aus dieser Registry —
 * nicht fest auf „Lesen/Fragen" verdrahtet. Ein Kurs ohne Fragen zeigt
 * einfach keinen Fragen-Umschalter.
 */
import type { Block } from "../data/types";

export interface CourseMode {
  /** Schlüssel, wie ihn der Kurs in Course.modes deklariert. */
  id: string;
  label: string;
  /** Kurzer Erklärtext (zeigt der Kurs-Screen als `.mode-hint`). */
  hint?: string;
  /** Filtert/ordnet die Blöcke für diesen Modus. */
  selectBlocks?: (blocks: Block[]) => Block[];
}

const registry = new Map<string, CourseMode>();

export function registerMode(mode: CourseMode): void {
  registry.set(mode.id, mode);
}

export function getMode(id: string): CourseMode | undefined {
  return registry.get(id);
}

/** Modi für die im Kurs deklarierten IDs, in deklarierter Reihenfolge. */
export function resolveModes(declared: string[]): CourseMode[] {
  return declared
    .map((id) => registry.get(id))
    .filter((m): m is CourseMode => Boolean(m));
}

// --- Standardmodi für v1 (Kurs 1) -----------------------------------------
// IDs 1:1 zur Web-App (`ki-kurs-modus-v2` = "fragen" | "normal").
//   normal = „Kurs normal" (Kapitel lesen, Lesetext-Streaming)
//   fragen = „Kurs über Fragen" (Fragenkarten, didaktisches Herzstück)
// Medien (image/video/audio/embed) rendert der ChapterPlayer in BEIDEN Modi
// separat oben — `selectBlocks` liefert nur den modus-spezifischen Inhalt.
registerMode({
  id: "normal",
  label: "Kurs normal",
  hint: "Lies das Kapitel ganz normal durch und stelle deine Fragen dem Chatbot.",
  selectBlocks: (blocks) => blocks.filter((b) => b.type === "text"),
});
registerMode({
  id: "fragen",
  label: "Kurs über Fragen",
  hint: "Lerne die Kursinhalte, indem du der KI die richtigen Fragen stellst.",
  selectBlocks: (blocks) => blocks.filter((b) => b.type === "questions"),
});
